import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import {
    RewardsAvailableRewardsPageWidgetModalDefinition,
    RewardsJoinNowModalWidgetDefinition,
    RewardsQrCodeModalWidgetDefinition,
    RewardsVerifyAccountModalWidgetDefinition,
    RewardsWaysToEarnPageWidgetModalDefinition,
    RewardsWaysToSpendPageWidgetModalDefinition,
} from "@apps/rewards/definitions";
import {
    rewardsAvailableRewardsPageWidgetModalToken,
    rewardsJoinNowModalWidgetToken,
    rewardsQrCodeModalWidgetToken,
    rewardsVerifyAccountModalWidgetToken,
    rewardsWaysToEarnPageWidgetModalToken,
    rewardsWaysToSpendPageWidgetModalToken,
} from "@apps/rewards/tokens";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { inject, singleton } from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";

import type {
    ModalConfig,
    RewardsModalNames,
    RewardsModalParams,
} from "./types";

@singleton()
class RewardsModalManager extends ModalManager {
    private readonly modalConfig: ModalConfig;

    constructor(
        @inject(DynamicImportModule)
        private readonly dynamicImportModule: DynamicImportModule
    ) {
        super();
        this.modalConfig = {
            [REWARDS_WIDGET_NAMES.REWARDS_VERIFY_ACCOUNT_MODAL_WIDGET]: {
                definition: RewardsVerifyAccountModalWidgetDefinition,
                token: rewardsVerifyAccountModalWidgetToken,
            },
            [REWARDS_WIDGET_NAMES.REWARDS_JOIN_NOW_MODAL_WIDGET]: {
                definition: RewardsJoinNowModalWidgetDefinition,
                token: rewardsJoinNowModalWidgetToken,
            },
            [REWARDS_WIDGET_NAMES.REWARDS_QR_CODE_MODAL_WIDGET]: {
                definition: RewardsQrCodeModalWidgetDefinition,
                token: rewardsQrCodeModalWidgetToken,
            },
            [REWARDS_WIDGET_NAMES.REWARDS_WAYS_TO_EARN_PAGE_WIDGET_MODAL]: {
                definition: RewardsWaysToEarnPageWidgetModalDefinition,
                token: rewardsWaysToEarnPageWidgetModalToken,
            },
            [REWARDS_WIDGET_NAMES.REWARDS_WAYS_TO_SPEND_PAGE_WIDGET_MODAL]: {
                definition: RewardsWaysToSpendPageWidgetModalDefinition,
                token: rewardsWaysToSpendPageWidgetModalToken,
            },
            [REWARDS_WIDGET_NAMES.REWARDS_AVAILABLE_REWARDS_PAGE_WIDGET_MODAL]:
                {
                    definition:
                        RewardsAvailableRewardsPageWidgetModalDefinition,
                    token: rewardsAvailableRewardsPageWidgetModalToken,
                },
        };

        this.registerModals();
    }

    private registerModals() {
        Object.values(this.modalConfig).forEach(({ token }) => {
            this.addModal(token);
        });
    }

    private async fetchModal(modalName: RewardsModalNames): Promise<void> {
        await this.dynamicImportModule.exports.dynamicImportManager.fetch({
            definition: this.modalConfig[modalName].definition,
        });
    }

    public async openRewardsModal(
        modalName: RewardsModalNames,
        params?: RewardsModalParams
    ): Promise<void> {
        await this.fetchModal(modalName);

        this.openModal(this.modalConfig[modalName].token, true, params);
    }
}

export default RewardsModalManager;
